<template>
	<div class="mb-4 stats-container">
		<div :class="[statGridClass, stream.type === 'scheduled' ? 'mt-1 md:mt-0' : 'mt-1 md:mt-4']"
			v-if="stream.type !== 'subsecond'"
			class="media-info md:mx-0 grid w-full bg-surface-2 rounded-xl overflow-hidden shadow-[0_2px_6px_rgba(0,0,0,0.28)] ring-1 ring-inset ring-white/[0.04]">
			<!-- bandwidth stats -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left"
				v-if="checkColumnVisibility('analytics') && stream.type !== 'restream'">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Bandwidth</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<span v-if="bandwidthStats.loaded">
						{{ bandwidthStats.value }}
						<span class="text-xs leading-4 text-surface-7 uppercase">{{ bandwidthStats.unit }}</span>
					</span>
					<span v-else
						class="inline-block size-3 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
				</div>
			</div>
			<!-- viewers stats -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left"
				v-if="(checkColumnVisibility('analytics')) && (stream.type !== 'restream')">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Viewers</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="streamAlive">
						<span v-if="clientsCount !== -1">{{ numberFormated(clientsCount) }}</span>
						<span v-else
							class="inline-block size-3 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
					</template>
					<span v-else class="text-surface-6" title="Waiting for stream data">—</span>
				</div>
			</div>
			<!-- stream bitrate (video primary, audio secondary) -->
			<div class="relative flex flex-col items-start justify-start px-3 py-3 text-left"
				v-if="checkColumnVisibility('stream_bitrate')">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Bitrate</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="streamAlive">
						<span v-if="mediaPulse.bitrate">
							{{ mediaPulse.bitrate }}
							<span class="text-xs leading-4 text-surface-7">kbps</span>
						</span>
						<span v-else class="inline-block size-3 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin" />
					</template>
					<span v-else class="text-surface-6" title="Waiting for stream data">—</span>
				</div>
				<div class="mt-1 text-xs leading-4 text-surface-7 whitespace-nowrap">
					Audio
					<!-- pulse carries a single audioBitrate (last track), so with
					     multiple audio tracks show the count instead of one
					     unrepresentative bitrate -->
					<template v-if="streamAlive && audioTrackCount > 1">
						<span class="text-surface-8 tabular-nums">{{ audioTrackCount }}</span> tracks
					</template>
					<template v-else-if="streamAlive && mediaPulse.audioBitrate">
						<span class="text-surface-8 tabular-nums">{{ mediaPulse.audioBitrate }}</span> kbps
					</template>
					<span v-else class="text-surface-6">—</span>
				</div>
				<Tooltip v-if="stream.type === 'live' || stream.type === 'restream'"
					content="Open input health charts" wrapper-class="absolute top-2 right-2">
					<button type="button" aria-label="Open input health charts"
						class="flex size-6 items-center justify-center rounded bg-transparent text-surface-7 hover:text-blue"
						@click="onClickBitrate">
						<icon-base width-view-box="16" height-view-box="16" iconColor="none" width="14" height="14">
							<icon-diagram />
						</icon-base>
					</button>
				</Tooltip>
			</div>
			<!-- media quality: resolution, fps & codecs combined -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left"
				v-if="checkColumnVisibility('media_quality')" :class="streamAlive ? 'stream-alive' : 'empty'">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Format</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="streamAlive && streamResolution">
						{{ streamResolution }}
						<span v-if="checkColumnVisibility('media_codecs') && mediaPulse.fps"
							class="text-xs leading-4 text-surface-7">{{ formatFPS(mediaPulse.fps) }} FPS</span>
					</template>
					<span v-else class="text-surface-6" title="Waiting for stream data">—</span>
				</div>
				<div v-if="streamAlive && checkColumnVisibility('media_codecs') && codecBadges.visible.length"
					class="mt-1 flex gap-x-1 whitespace-nowrap">
					<Badge v-for="(group, index) in codecBadges.visible" inline size="xs" :key="index"
						class="uppercase" :type="group.type">
						{{ group.codec }}<template v-if="group.count > 1"> ×{{ group.count }}</template>
					</Badge>
					<Tooltip v-if="codecBadges.overflow.length" :content="codecOverflowTitle">
						<Badge inline size="xs" type="main">+{{ codecBadges.overflow.length }}</Badge>
					</Tooltip>
				</div>
			</div>
			<!-- videos count -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left"
				v-if="checkColumnVisibility('videos_count')">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Total Files</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					{{ videoFiles.length }}
				</div>
			</div>
			<!-- bytes storage -->
			<div class="flex flex-col items-start justify-start px-3 py-3 text-left"
				v-if="checkColumnVisibility('bytes_storage')">
				<h6 class="mb-1 text-xs leading-4 font-medium text-surface-7">Storage</h6>
				<div class="text-base leading-5 font-medium text-surface-9 tabular-nums whitespace-nowrap">
					<template v-if="videoFiles.length">
						{{ getStorageused().totalBytes }}
						<span class="text-xs leading-4 text-surface-7 uppercase">{{ getStorageused().unit }}</span>
					</template>
					<template v-else-if="videoFiles.length === 0">0</template>
					<span v-else
						class="inline-block size-3 align-middle rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import StreamService from '@/services/StreamService';
import MetricsService from '@/services/MetricsService';
import { bytes, normalizedFPS, numberFormat } from '@/filtersNew';
import IconBase from '@/components/icon/IconBase.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import IconDiagram from '@/components/icon/IconDiagram.vue';
import Badge from '@/components/Badge.vue';

export default {
	name: 'StreamOverviewStats',
	components: {
		IconBase,
		Tooltip,
		IconDiagram,
		Badge
	},
	props: {
		stream: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id');
			}
		},
		videoFiles: {
			type: Array,
			default() { return []; }
		},
		mediaPulse: {
			type: Object,
			default() { return {}; }
		}
	},
	data() {
		return {
			scopeAlive: true,
			processing: true,
			processingMessage: null,
			windowHeight: 0,
			clientsCount: -1,
			totalEncodingStorageTime: 0,
			bandwidthStats: {
				value: 0,
				unit: 'bytes',
				loaded: false
			},
			bandwidthPollTimerId: null,
			viewershipPollTimerId: null,
			columns: [
				{ id: 'stream_status', streamTypes: ['restream'] },
				{ id: 'analytics', streamTypes: ['live', 'ipcam', 'vod'] },
				{
					id: 'stream_bitrate',
					streamTypes: ['restream', 'live', 'ipcam', 'scheduled']
				},
				{ id: 'media_quality', streamTypes: ['restream', 'live', 'ipcam', 'scheduled'] },
				{ id: 'media_codecs', streamTypes: ['restream', 'live', 'ipcam'] },
				{ id: 'videos_count', streamTypes: ['vod'] },
				{ id: 'bytes_storage', streamTypes: ['vod'] }
			]
		};
	},
	computed: {
		subscription() {
			let subs = this.$store.state.User.subscriptions.find(sub => sub.category && sub.category.includes(this.stream.type));
			if (!subs && this.stream.type === 'vod') {
				subs = this.$store.state.User.subscriptions.find(sub => sub.package && sub.package.definition.maxVodItems && parseInt(sub.package.definition.maxVodItems, 10) > 0);
			}
			return subs;
		},
		streamAlive() {
			return this.stream.enabled && this.mediaPulse && this.mediaPulse.alive;
		},
		streamResolution() {
			return (this.streamAlive && this.mediaPulse.width) ? `${this.mediaPulse.width} × ${this.mediaPulse.height}` : null;
		},
		audioTrackCount() {
			const codecs = (this.mediaPulse && this.mediaPulse.codecs) || [];
			return codecs.filter(track => track && track.type === 'audio').length;
		},
		// pulse.codecs has one entry per track, so multi-language streams
		// (e.g. 10 AAC audio tracks) are deduped into "AAC ×10". The visible
		// set is hard-capped so the nowrap badge row can never clip, even in
		// the narrow 2×2 layout; the remainder collapses into a "+N" badge.
		codecBadges() {
			const codecs = (this.mediaPulse && this.mediaPulse.codecs) || [];
			const groups = [];
			codecs.forEach((track) => {
				if (!track || !track.codec) return;
				const type = track.type ?? 'main';
				const found = groups.find(g => g.type === type && g.codec === track.codec);
				if (found) found.count += 1;
				else groups.push({ type, codec: track.codec, count: 1 });
			});
			const MAX_VISIBLE = 2;
			if (groups.length <= MAX_VISIBLE) return { visible: groups, overflow: [] };
			return { visible: groups.slice(0, MAX_VISIBLE), overflow: groups.slice(MAX_VISIBLE) };
		},
		codecOverflowTitle() {
			return this.codecBadges.overflow
				.map(g => `${String(g.codec).toUpperCase()}${g.count > 1 ? ` ×${g.count}` : ''}`)
				.join(', ');
		},
		visibleStatCells() {
			let count = 0;
			if (this.checkColumnVisibility('analytics') && this.stream.type !== 'restream') count += 2;
			if (this.checkColumnVisibility('stream_bitrate')) count += 1;
			if (this.checkColumnVisibility('media_quality')) count += 1;
			if (this.checkColumnVisibility('videos_count')) count += 1;
			if (this.checkColumnVisibility('bytes_storage')) count += 1;
			return count;
		},
		statGridClass() {
			// column templates live in the scoped @container rules below —
			// the stats column width does not track the viewport, so
			// viewport breakpoints cannot decide when to collapse to 2×2
			if (this.visibleStatCells >= 4) {
				// weighted columns for the live layout (Bandwidth / Viewers / Bitrate / Quality),
				// equal columns for other 4-cell layouts (e.g. VOD)
				return this.checkColumnVisibility('media_quality') ? 'stat-cols-weighted' : 'stat-cols-4';
			}
			if (this.visibleStatCells === 3) return 'stat-cols-3';
			return 'stat-cols-2';
		}
	},
	mounted() {
		if (this.stream.type === 'live' || this.stream.type === 'ipcam') {
			this.setupBandwidthCounter();
			this.setupViewershipCounter();
		} else if (this.stream.type === 'vod') {
			this.setupBandwidthCounter();
			this.setupViewershipCounter();
		}
	},
	beforeUnmount() {
		this.scopeAlive = false;
		clearTimeout(this.bandwidthPollTimerId);
		clearTimeout(this.viewershipPollTimerId);
		this.bandwidthPollTimerId = null;
		this.viewershipPollTimerId = null;
	},
	methods: {
		onClickBitrate() {
			if (this.stream.type === 'live' || this.stream.type === 'restream') this.$router.push({ name: 'StreamsManageAnalyticsInputHealthCharts' })
		},
		checkColumnVisibility(colId) {
			const col = this.columns.find(c => c.id === colId);
			if (col) {
				return col.streamTypes.indexOf(this.stream.type) > -1;
			}
		},
		scheduleCounterPoll(timerKey, callback, delayMs) {
			if (!this.scopeAlive) return;
			clearTimeout(this[timerKey]);
			this[timerKey] = setTimeout(() => {
				this[timerKey] = null;
				callback();
			}, delayMs);
		},
		setupViewershipCounter() {
			const updateCounter = async () => {
				if (!this.scopeAlive) return;

				let timeoutInterval = 5000;
				let shouldUpdateValue = this.stream.enabled;
				if (document.hidden) {
					shouldUpdateValue = false;
				}

				if (shouldUpdateValue) {
					if (!document.hasFocus()) {
						timeoutInterval *= 6;
					}
				}

				if (shouldUpdateValue) {
					try {
						const data = await StreamService.getStreamViewership(
							this.stream.key
						);
						if (!this.scopeAlive) return;
						this.clientsCount = data.viewers || 0;
					} catch (e) { }
				}

				this.scheduleCounterPoll('viewershipPollTimerId', updateCounter, timeoutInterval);
			};
			updateCounter();
		},
		setupBandwidthCounter() {
			const updateCounter = async () => {
				if (!this.scopeAlive) return;

				let timeoutInterval = 10000;
				let shouldUpdateValue =
					this.stream.enabled || !this.bandwidthStats.loaded;
				if (document.hidden) {
					shouldUpdateValue = false;
				}

				if (shouldUpdateValue) {
					if (!document.hasFocus()) {
						timeoutInterval *= 6;
					}
				}

				if (this.stream.type === 'vod') {
					timeoutInterval = 60000;
				}

				if (shouldUpdateValue) {
					try {
						let startTime = this.subscription.cstart;
						if (_.isString(startTime)) {
							startTime = new Date(startTime).getTime();
							if (this.subscription.enabled === false) {
								startTime = moment(startTime).utc().startOf('day').valueOf()
							}
						}

						const bandwidthRes = await MetricsService.getStreamBandwidth(
							this.stream._id,
							startTime
						);
						if (!this.scopeAlive) return;
						const downloadedBytes = _.get(bandwidthRes, 'bytes') || 0;
						const bytesInfo = bytes(
							downloadedBytes >= 100000000 ? downloadedBytes : 0, // only show bandwidth > 100MB
							true,
							2,
							true
						);
						this.bandwidthStats = _.assign(
							{},
							this.bandwidthStats,
							bytesInfo
						);
						this.bandwidthStats.loaded = true;
					} catch (e) {

					}
				}

				this.scheduleCounterPoll('bandwidthPollTimerId', updateCounter, timeoutInterval);
			};
			updateCounter();
		},
		getStorageused() {
			let totalBytes = this.videoFiles.reduce((tf, item) => tf + item.bytes, 0);
			const k = 1000;
			const decimals = 2;
			const dm = decimals < 0 ? 0 : decimals;
			const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
			const i = Math.floor(Math.log(totalBytes) / Math.log(k));
			totalBytes = parseFloat((totalBytes / Math.pow(k, i)).toFixed(dm));
			return {
				totalBytes: totalBytes,
				unit: sizes[i]
			}
		},
		formatFPS(number) {
			return normalizedFPS(number)
		},
		numberFormated(number) {
			return numberFormat(number)
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* The stats column width does not track the viewport (xl page grid vs
   flex-wrap below xl), so layout is driven by native CSS container
   queries on the bar's wrapper instead of viewport breakpoints. */
.stats-container {
	container-type: inline-size;
}

/* Narrow container (default): 2×2 grid with a center vertical divider
   and horizontal row dividers. nth-child only counts rendered cells,
   so per-stream-type visibility keeps working. */
.media-info {
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.media-info > :nth-child(even) {
	border-left: 1px solid var(--surface-3);
}

.media-info > :nth-child(n+3) {
	border-top: 1px solid var(--surface-3);
}

/* Wide container: one row, vertical dividers only */
@container (min-width: 560px) {
	.media-info.stat-cols-weighted {
		/* Format hugs its content (max-content floor stops clipping at the
		   560px threshold); its former slack goes to Bandwidth/Bitrate */
		grid-template-columns: 1.15fr 0.8fr 1.35fr minmax(max-content, 1.2fr);
	}

	.media-info.stat-cols-4 {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}

	.media-info.stat-cols-3 {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}

	.media-info > :nth-child(n+2) {
		border-left: 1px solid var(--surface-3);
		border-top: 0;
	}
}

/* Roomier cell padding once the bar is comfortably wide */
@container (min-width: 800px) {
	.media-info > * {
		padding-left: 16px;
		padding-right: 16px;
	}
}
</style>
